import { styled } from 'styled-components';
import { Link } from 'react-router-dom';
import Tooltip from '../../tooltip';
import { colors, spacing } from '../../../style';
import { clickableTableCellVariants } from '../table';
import { BoxProps } from '../../box';
import { getCssRuleWithResponsiveCalculation } from '../../../utils';
import TableCell, { TableCellProps } from './table-cell';

interface StyledClickableButtonProps {
  $variant?: keyof typeof clickableTableCellVariants;
  $padding: keyof typeof spacing;
  $paddingLeft?: keyof typeof spacing;
  $paddingRight?: keyof typeof spacing;
  $justifyContent?: BoxProps['justifyContent'];
  $alignItems?: BoxProps['alignItems'];
  $flexDirection?: BoxProps['flexDirection'];
  disabled?: boolean;
}

const StyledClickableButton = styled.button<StyledClickableButtonProps>`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: ${props => spacing[props.$padding]};
  padding-left: ${props => props.$paddingLeft && spacing[props.$paddingLeft]};
  padding-right: ${props => props.$paddingRight && spacing[props.$paddingRight]};
  transition: all 160ms ease;
  cursor: pointer;
  text-decoration: none;
  font: inherit;
  text-align: left;
  ${props =>
    getCssRuleWithResponsiveCalculation<BoxProps['justifyContent']>({
      property: 'justify-content',
      value: props.$justifyContent,
    })};
  ${props =>
    getCssRuleWithResponsiveCalculation<BoxProps['alignItems']>({
      property: 'align-items',
      value: props.$alignItems,
    })};
  ${props =>
    getCssRuleWithResponsiveCalculation<BoxProps['flexDirection']>({
      property: 'flex-direction',
      value: props.$flexDirection,
    })};
  ${props => props.$variant && clickableTableCellVariants[props.$variant]};

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${colors.sky};
  }
`;

export interface ClickableTableCellProps extends Omit<TableCellProps, 'variant'> {
  variant?: keyof typeof clickableTableCellVariants;
  justifyContent?: BoxProps['justifyContent'];
  alignItems?: BoxProps['alignItems'];
  flexDirection?: BoxProps['flexDirection'];
  to?: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  toolTipVariant?: 'transparent';
}

export const ClickableTableCell = ({
  to,
  href,
  onClick,
  disabled,
  padding = 'sm',
  paddingLeft,
  paddingRight,
  variant,
  justifyContent,
  alignItems,
  flexDirection,
  tooltip,
  children,
  style,
  toolTipVariant,
  ...cellProps
}: ClickableTableCellProps) => {
  const Component: React.ElementType = !disabled ? (to ? Link : href ? 'a' : 'button') : 'button';

  // Prevent navigation if disabled
  const finalHref = disabled ? undefined : href;
  const finalTo = disabled ? undefined : to;
  const buttonType = Component === 'button' ? 'button' : undefined;

  const button = (
    <StyledClickableButton
      as={Component}
      href={finalHref}
      to={finalTo}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      $variant={variant}
      $padding={padding}
      $paddingLeft={paddingLeft}
      $paddingRight={paddingRight}
      $justifyContent={justifyContent}
      $alignItems={alignItems}
      $flexDirection={flexDirection}
      type={buttonType}
    >
      {children}
    </StyledClickableButton>
  );

  return (
    <TableCell {...cellProps} padding={'none'} paddingLeft={'none'} paddingRight={'none'} style={style}>
      {tooltip ? (
        <Tooltip text={tooltip} variant={toolTipVariant} containerComponent="div">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </TableCell>
  );
};
